import { useAuth } from '@/auth/AuthProvider'
import type { UserProfile } from '@/types/domain'

/**
 * Whether the signed-in person is an administrator.
 *
 * This is the one rule the retired admin portal's `usePermissions` hook
 * carried (`user?.role === 'admin'`), lifted out before that directory was
 * deleted. Nothing else in it was worth keeping: `canAccessRoute` matched a
 * `/admin/...` prefix list that went with those routes, and
 * `getRoleDisplayName` said "User" in English on a German screen.
 *
 * It decides **presentation** only. The gateway checks the role on every
 * `/v1/admin/*` call it serves; hiding a tab keeps a screen from lying about
 * what it can show, it does not keep anyone out.
 */
export function isAdminRole(role: UserProfile['role'] | undefined): boolean {
  return role === 'admin'
}

export function useIsAdmin(): { isAdmin: boolean; loading: boolean } {
  const { user, loading } = useAuth()
  // While the bootstrap refresh is still running there is no user yet, and an
  // administrator reloading an admin-only URL would be told they have no
  // access. "Not yet known" is not "no".
  return { isAdmin: isAdminRole(user?.role), loading }
}
